// The public proforma / request-tracking surface, reached at
// /proforma/:token (the SPA route — the /track/... API path is proxied
// straight to the backend, so the client route can't live there). Every
// call here is genuinely unauthenticated: the tracking token in the URL is
// the only credential.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const viewKey = (token) => ['tracking', token];
const notesKey = (token) => ['tracking', token, 'notes'];

// GET /track/:token — the full buyer-safe tracking view (status, timeline,
// quotation + lines when viewable, payment request, upload status).
export function useProformaView(token) {
  return useQuery({
    queryKey: viewKey(token),
    queryFn: () => apiClient.get(`/track/${token}`),
    enabled: Boolean(token),
    retry: false,
  });
}

// POST /track/:token/review — the whole in-page decision. `decisions` is
// [{ lineNo, decision, declineCategory?, declineNote?, newQty? }]. The
// response is the refreshed view plus `reviewOutcome`
// ('accepted' | 'declined' | 'revised') — written straight into the cache
// so the page re-renders the new state with no refetch.
export function useSubmitReview(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (decisions) => apiClient.post(`/track/${token}/review`, { decisions }),
    onSuccess: (data) => queryClient.setQueryData(viewKey(token), data),
  });
}

// POST /track/:token/accept — one-click accept of every line as-is.
export function useAcceptProforma(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/track/${token}/accept`),
    onSuccess: (data) => queryClient.setQueryData(viewKey(token), data),
  });
}

// POST /track/:token/request-updated-prices — the one action a buyer with an
// EXPIRED proforma can still take: ask sales to re-price. Sends the request
// back to pricing; the refreshed view comes back showing 'being_priced'.
export function useRequestUpdatedPrices(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/track/${token}/request-updated-prices`),
    onSuccess: (data) => queryClient.setQueryData(viewKey(token), data),
  });
}

// POST /track/:token/cancel — the buyer withdraws a request before any
// proforma exists (buyerStatus 'received' or 'being_priced' only; once a
// proforma has been sent, decline is the right action instead). Same
// refreshed-view-into-cache pattern as every other action above.
export function useCancelRequest(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason) => apiClient.post(`/track/${token}/cancel`, { reason }),
    onSuccess: (data) => queryClient.setQueryData(viewKey(token), data),
  });
}

// GET /track/:token/notes -> { notes: [...] }. `select` unwraps it so the
// component sees the array; every writer below must therefore cache the
// WHOLE { notes } envelope, not the bare array, or `select` re-runs on an
// array and yields undefined.
export function useProformaNotes(token) {
  return useQuery({
    queryKey: notesKey(token),
    queryFn: () => apiClient.get(`/track/${token}/notes`),
    select: (data) => data.notes,
    enabled: Boolean(token),
    retry: false,
  });
}

export function useAddProformaNote(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiClient.post(`/track/${token}/notes`, { body }),
    onSuccess: (data) => queryClient.setQueryData(notesKey(token), data),
  });
}

// POST /track/:token/verify/request — step 1 of the OTP gate in front of
// GET /track/:token (TrackingGate.jsx): the holder of the link proves they
// also control the inbox the proforma was sent to. ALWAYS responds
// { sent: true } regardless of whether the email matched the request's
// contact/submitter email — never reveals which, same contract as
// POST /guest/otp/request.
export function useRequestGateOtp(token) {
  return useMutation({
    mutationFn: (email) => apiClient.post(`/track/${token}/verify/request`, { email }),
  });
}

// POST /track/:token/verify — step 2: the 6-digit code against that same
// email. On success the gate is open server-side (verified_until is set);
// invalidating the view query (rather than writing into its cache, since
// this mutation's own response shape, { verified: true }, isn't the view
// shape) makes useProformaView refetch GET /track/:token, which now returns
// the real view instead of { verificationRequired: true, ... } — the page
// re-renders on its own, no other plumbing needed.
export function useVerifyGateOtp(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, code }) => apiClient.post(`/track/${token}/verify`, { email, code }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: viewKey(token) }),
  });
}

// POST /track/lookup — the guest fallback: a submitter with no account and
// no tracking link enters their reference number + the email on the
// request. Returns the same buyer-safe tracking view GET /track/:token
// does (including `trackingToken`, so the result can deep-link to the PDF
// downloads and the /proforma/:token review page). 404
// TRACKING_TOKEN_NOT_FOUND on any mismatch (never leaks which half failed),
// 429 TRACKING_LOOKUP_RATE_LIMITED after too many wrong guesses for one ref.
export function useTrackingLookup() {
  return useMutation({
    mutationFn: ({ refNo, email }) => apiClient.post('/track/lookup', { refNo, email }),
  });
}
